import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Link2, Loader, RefreshCw, Unlink } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import SocialPlatformIcon from './SocialPlatformIcon';
import { social as socialApi } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const PLATFORM_LABELS = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  instagram: 'Instagram'
};

const SocialConnections = ({ compact = false, onChange }) => {
  const { supportSession } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingPlatform, setWorkingPlatform] = useState('');

  const loadConnections = useCallback(async () => {
    try {
      const response = await socialApi.getConnections();
      setPlatforms(response.data.platforms || []);
      onChange?.(response.data.platforms || []);
    } catch (error) {
      console.error('Load social connections error:', error);
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    const status = searchParams.get('social');
    if (!status) return;
    if (status === 'connected') {
      toast.success(`${searchParams.get('platform') || 'Social account'} connected successfully!`);
      loadConnections();
    } else if (status === 'error') {
      toast.error(searchParams.get('message') || 'Social account could not be connected');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('social');
    next.delete('platform');
    next.delete('message');
    setSearchParams(next, { replace: true });
  }, [loadConnections, searchParams, setSearchParams]);

  const handleConnect = async (platform) => {
    setWorkingPlatform(platform);
    try {
      const response = await socialApi.connect(platform);
      window.location.assign(response.data.authorizationUrl);
    } catch (error) {
      toast.error(error.response?.data?.error || `Could not connect ${PLATFORM_LABELS[platform]}`);
      setWorkingPlatform('');
    }
  };

  const handleDisconnect = async (platform) => {
    const label = PLATFORM_LABELS[platform];
    if (!window.confirm(`Disconnect ${label}? You can reconnect it at any time.`)) return;
    setWorkingPlatform(platform);
    try {
      await socialApi.disconnect(platform);
      toast.success(`${label} disconnected`);
      await loadConnections();
    } catch (error) {
      toast.error(error.response?.data?.error || `Could not disconnect ${label}`);
    } finally {
      setWorkingPlatform('');
    }
  };

  if (loading) {
    return <div className="flex min-h-24 items-center justify-center"><Loader className="animate-spin text-primary-500" size={24} /></div>;
  }

  const anyConfigured = platforms.some(item => item.configured);

  return (
    <section id="connected-social-accounts" className={compact ? '' : 'rounded-xl border border-gray-100 bg-white p-6 shadow-sm'}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <Link2 size={20} />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900">Connected Social Accounts</h2>
          <p className="mt-1 text-sm leading-5 text-gray-500">
            Connect professional accounts once, then publish a branded flyer directly from the hub. TTIC never sees your social password.
          </p>
        </div>
      </div>

      {supportSession && <p className="mt-4 rounded-lg bg-primary-50 p-3 text-sm text-primary-800">Social connections are read-only during support access. Return to your account before changing them.</p>}
      <div className="mt-5 space-y-3">
        {platforms.map(item => {
          const label = PLATFORM_LABELS[item.platform];
          const working = workingPlatform === item.platform;
          return (
            <div key={item.platform} className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                  <SocialPlatformIcon platform={item.platform} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{label}</p>
                  <p className={`truncate text-sm ${item.connected ? 'text-green-700' : 'text-gray-500'}`}>
                    {item.connected
                      ? `Connected to ${item.accountName}`
                      : item.needsReconnect
                        ? 'Connection expired — reconnect to publish'
                        : item.configured
                          ? 'Not connected'
                          : 'Awaiting TTIC platform approval'}
                  </p>
                </div>
              </div>
              {item.connected ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><CheckCircle2 size={15} /> Ready</span>
                  <button type="button" onClick={() => handleDisconnect(item.platform)} disabled={working || Boolean(supportSession)} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-600 hover:border-red-200 hover:text-red-600 disabled:opacity-50">
                    {working ? <Loader className="animate-spin" size={16} /> : <Unlink size={16} />} Disconnect
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => handleConnect(item.platform)}
                  disabled={!item.configured || working || Boolean(supportSession)}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                >
                  {working ? <Loader className="animate-spin" size={16} /> : item.needsReconnect ? <RefreshCw size={16} /> : <ExternalLink size={16} />}
                  {item.needsReconnect ? `Reconnect ${label}` : `Connect ${label}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!anyConfigured && (
        <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800">
          Direct publishing is built and ready. TTIC’s LinkedIn and Meta developer credentials and platform approval are the remaining activation steps. Download, copy, and device sharing continue to work in the meantime.
        </p>
      )}
    </section>
  );
};

export default SocialConnections;
